import React, { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useMst } from '../../model/Root';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Table from '../../components/Table/Table';
import AddButton from '../../components/Button/AddButton';
import { useTranslation } from 'react-i18next';
import { PROGRAM_TABLE } from '../../utils/constants';
import { Community, LinkRow, Row } from '../../types';
import AlertModal from '../../components/AlertModal';

const ProgramScreen = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    userStore: { communities, user, getCommunities },
    programStore: { programs, getAllPrograms, deleteProgram },
  } = useMst();
  const [selectedRow, setSelectedRow] = useState<Row | LinkRow>();
  const [result, setResult] = useState<Row[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const filterPrograms = useCallback(
    (communities: Community[], ownCommunities: Community[]) => {
      const filtered = communities.filter((community: Community) => {
        return ownCommunities.some((el) => {
          return el.id === community.id;
        });
      });
      const filteredPrograms = programs.filter((program) => {
        return filtered.find(({ community }) => community === program.publisher);
      });

      return filteredPrograms;
    },
    [programs]
  );

  useEffect(() => {
    const getPrograms = async () => {
      await getAllPrograms();

      if (user?.admin) {
        const tempResult: Row[] =
          (programs?.length ?? 0) > 0
            ? programs.map((program) => ({
                id: program.id,
                name: program.name,
                value: `${program.name} - ${program.author}`,
                secondValue: program.published ? t('published') : t('draft'),
              }))
            : [];
        setResult(tempResult);
      } else {
        if (communities && user?.communities.length) {
          const filtered = filterPrograms(communities, user.communities);
          const tempResult: Row[] =
            filtered && filtered.length > 0
              ? filtered.map((program) => ({
                  id: program.id,
                  name: program.name,
                  value: `${program.name} - ${program.author}`,
                  secondValue: program.published ? t('published') : t('draft'),
                }))
              : [];
          setResult(tempResult);
        }
      }
    };

    getPrograms();
  }, [getAllPrograms, getCommunities, user, communities, programs, t, filterPrograms]);

  useEffect(() => {
    if (!selectedRow?.id || modalOpen) return;
    navigate(`/programs/${selectedRow.id}`);
  }, [selectedRow?.id, modalOpen, navigate]);

  const navigateTo = () => {
    navigate('/programs/create');
  };

  const deleteBook = async () => {
    if (selectedRow?.id) {
      await deleteProgram(selectedRow.id.toString());
      await getAllPrograms();
      const tempResult: Row[] =
        programs.length > 0
          ? programs.map((program) => ({
              id: program.id,
              name: program.name,
              value: `${program.name} - ${program.author}`,
              secondValue: program.published ? t('published') : t('draft'),
            }))
          : [];
      setResult(tempResult);
      setSelectedRow(undefined);
    }
    setModalOpen(false);
  };

  const toggleDelete = (id: number) => {
    setModalOpen(true);
    setSelectedRow({ id: id });
  };

  const cancel = () => {
    setSelectedRow(undefined);
    setModalOpen(false);
  };

  return (
    <Layout title={t('programs.title')}>
      <AddButton onClick={navigateTo} text={t('programs.add_btn')} />
      <Table
        tableType={PROGRAM_TABLE}
        data={result}
        setSelectedRow={(value) => setSelectedRow({ id: value?.id, value: value?.value })}
        selectedRow={selectedRow}
        onDelete={(id) => toggleDelete(id)}
      />
      <AlertModal
        open={modalOpen}
        title="programs.delete"
        onClose={() => setModalOpen(!modalOpen)}
        leftButtonPress={cancel}
        rightButtonPress={deleteBook}
      />
    </Layout>
  );
});

export default ProgramScreen;
